import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
This guide walks through building **autonomous agents** on top of CloudQuote — workflows that run on a schedule (or on-demand) and combine multiple endpoints to produce actionable output without a human in the loop.

If you're new to the AI surface, read [Chat API](/docs/chat-api) and [MCP](/docs/mcp) first. This page assumes you're comfortable with at least one of them.

## What an agent looks like

An agent is just a small program that does three things in a loop:

1. **Gather** information from CloudQuote (search, quotes, news, fundamentals).
2. **Reason** over the gathered data using an LLM (\`/chat\` or MCP).
3. **Act** — send a notification, write to a database, place an order, or update a dashboard.

The CloudQuote AI layer covers steps 1 and 2; step 3 is your code.

## Pattern 1: Scheduled research agent

A cron-triggered agent that scans for interesting opportunities and emails a daily digest.

### Sketch

\`\`\`text
08:00 UTC daily:
  results = POST /search/query {
    vectorQuery: { query: "energy companies missing earnings estimates", topK: 20 },
    filters: { datasets: ["news", "fundamentals"], dateRange: last_two_weeks }
  }

  for each company in results:
    chat: "For \${company}, fetch today's quote, YTD performance, and summarize news sentiment."
    # /chat orchestrates getQuote, getPriceHistory, getNewsByTicker

  shortlist = companies with intraday decline > 10% AND negative sentiment
  email shortlist to analysts@firm.com
\`\`\`

### Key endpoints

- [\`POST /search/query\`](/docs/search-api) — semantic discovery.
- [\`POST /chat\`](/docs/chat-api) — multi-step orchestration.
- Or [\`POST /mcp/invoke\`](/docs/mcp) if you're running an on-prem LLM.

## Pattern 2: Conversational research portal

A web application where retail or institutional users ask questions and the agent does the work.

### User says

> "Recommend the top 5 S&P 500 stocks with P/E below 15, rising dividends, and a positive 50-day SMA cross."

### What \`/chat\` does

1. Pull the S&P 500 universe via an internal helper.
2. For each ticker, call \`getFundamentals\` and filter on P/E and dividend trend.
3. For survivors, call \`getTechnicalIndicators\` and check 50/200-day SMA cross.
4. Return a ranked list with one-sentence rationales per name.

The user's *next* question — *"Show me a 1-year chart for IBM with the 50-day SMA"* — uses the conversation context: the model already knows IBM is one of its picks.

## Pattern 3: Algorithmic strategy builder

A quant team uses CloudQuote both as a data source and as a strategy-discovery tool.

\`\`\`text
1. Search:   /search/query  →  "bullish breakout patterns in mid-cap tech stocks"
2. Backtest: for each ticker, MCP getPriceHistory → compute breakout signals
3. Tune:     /chat "Suggest a lookback window that maximizes Sharpe ratio"
4. Execute:  MCP placeOrder  (if your account has the trading extension)
5. Monitor:  /chat "What is my current exposure and how many breakouts are pending?"
\`\`\`

The combination of vector search (for *concept*-driven discovery) and MCP (for repeatable, scriptable tool calls) is the part that makes this loop possible.

## Pattern 4: Risk scoring engine

An internal risk system enriches its proprietary client data with public news and fundamentals.

\`\`\`text
1. Search:   "supply disruptions affecting our counterparties" → companies in supplier network with recent news
2. Enrich:   for each flagged company, MCP getFundamentals + getTechnicalIndicators
3. Score:    combine news severity + financial health → risk score
4. Alert:    if score > threshold, log event in CRM and email risk managers
\`\`\`

## Pattern 5: Event-driven alert agent

A long-running agent subscribed to a topic, not a ticker.

\`\`\`text
On every new article in NewsRPM:
  similarity = vector_match(article, "supply chain disruptions affecting semiconductor factories")
  if similarity > 0.82:
    POST /webhook/alerts { article, similarity }
\`\`\`

This is the **semantic alert** pattern — much more flexible than ticker-based alerts because it triggers on *concepts* rather than literal symbol mentions.

## Implementation tips

- **Cache liberally.** Quote data is fine for a few seconds; fundamentals are fine for a day. Caching keeps you under rate limits.
- **Use \`getSpec\`** to register tool schemas in your MCP client automatically; that way new endpoints become available to the model without you redeploying.
- **Bound your loops.** LLMs can call tools forever if you let them. Set a max tool-call count per session and a token budget.
- **Log every tool call.** \`/chat\` and MCP both expose per-session usage so you can audit what your agent did.
- **Pin the model.** Use a specific \`model\` value (e.g., \`cloudquote-chat-1\`) instead of \`latest\` so behavior doesn't drift under you.

## Where to go next

- [Chat API](/docs/chat-api) — when you want CloudQuote to host the LLM.
- [MCP](/docs/mcp) — when you want to host the LLM yourself.
- [Search API](/docs/search-api) — the discovery primitive most agents start with.
`;

export default function AgentsPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "agents")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
